import type { Breadcrumb, MenuItem } from '@velasca/types'

/**
 * Recursively finds a path to the target handle in a menu tree.
 *
 * @param tree - The menu tree.
 * @param targetHandle - The target handle to search for.
 * @returns The path to the target handle or null if not found.
 */
export const traverseMenu = (
  tree: MenuItem[],
  targetHandle: string,
): Breadcrumb[] | null => {
  /**
   * Helper function to traverse the tree recursively.
   */
  const traverse = (node: MenuItem): Breadcrumb[] | null => {
    if (targetHandle === '') {
      return null
    }

    if (node.handle === targetHandle) {
      return [{ text: node.title, link: node.url }] // Found the target node
    }

    if (node.children) {
      for (const child of node.children) {
        const path = traverse(child)

        if (path) {
          return [{ text: node.title, link: node.url }, ...path] // Prepend current node to the path
        }
      }
    }

    return null // Handle not found in this branch
  }

  // Iterate through the root nodes of the tree
  for (const rootNode of tree) {
    const result = traverse(rootNode)

    if (result) {
      return result
    }
  }

  return null // Target handle not found in the tree
}
